import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

/** Per-route metadata: the page title (without the site suffix), a description, and its path. */
export interface PageSeo {
    title: string;
    description: string;
    /** Route path as the sitemap lists it, e.g. '/' or '/aboutus'. */
    path: string;
}

export const SITE_URL = 'https://eszes.in';
export const SITE_NAME = 'ESZES Consultancy & Services';
const DEFAULT_IMAGE = SITE_URL + '/assets/layout/images/branding/eszescs/logo-colored-light.png';

/**
 * Writes the document title, meta description, Open Graph / Twitter tags and
 * the canonical link for the current route. Every `ws-*` page calls `set()`
 * from `ngOnInit`; `index.html` carries the same tags for the home page so a
 * crawler that does not run JavaScript still sees them.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
    constructor(private title: Title, private meta: Meta, @Inject(DOCUMENT) private doc: Document) { }

    set(page: PageSeo): void {
        const path = page.path === '/' ? '/' : page.path.replace(/\/+$/, '');
        const url = SITE_URL + path;
        const title = page.title.includes('ESZES') ? page.title : `${page.title} | ESZES`;

        this.title.setTitle(title);
        this.meta.updateTag({ name: 'description', content: page.description });

        this.meta.updateTag({ property: 'og:type', content: 'website' });
        this.meta.updateTag({ property: 'og:site_name', content: SITE_NAME });
        this.meta.updateTag({ property: 'og:title', content: title });
        this.meta.updateTag({ property: 'og:description', content: page.description });
        this.meta.updateTag({ property: 'og:url', content: url });
        this.meta.updateTag({ property: 'og:image', content: DEFAULT_IMAGE });

        this.meta.updateTag({ name: 'twitter:card', content: 'summary' });
        this.meta.updateTag({ name: 'twitter:title', content: title });
        this.meta.updateTag({ name: 'twitter:description', content: page.description });

        let canonical = this.doc.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
        if (!canonical) {
            canonical = this.doc.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            this.doc.head.appendChild(canonical);
        }
        canonical.setAttribute('href', url);
    }
}
